import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

// Enable Immer's MapSet plugin for Set<string> support in loadingFiles
enableMapSet();
import {
  ScriptEditorState,
  ScriptEditorActions,
  ScriptFile,
  OpenTab,
  DiagnosticMessage,
  CompilationResult,
  OutputMessage,
  SearchResult,
  SidebarTab,
  BottomTab,
  FileSystemNode,
  DEFAULT_EDITOR_SETTINGS,
} from '../../core/types/script-editor.types';

const generateId = () => Math.random().toString(36).substring(2, 11);

const initialState: ScriptEditorState = {
  files: {},
  openTabs: [],
  activeTabId: null,
  loadingFiles: new Set<string>(),

  // File system tree - empty by default, populated when files are created
  fileTree: {
    'root-scripts': {
      id: 'root-scripts',
      name: 'scripts',
      path: 'assets/scripts',
      type: 'folder',
      children: [],
      parentId: null,
    },
  },
  rootFolderId: 'root-scripts',

  sidebarVisible: true,
  sidebarWidth: 250,
  activeSidebarTab: 'explorer',

  bottomPanelVisible: true,
  bottomPanelHeight: 200,
  activeBottomTab: 'problems',

  diagnostics: [],
  diagnosticCounts: { errors: 0, warnings: 0, infos: 0 },

  isCompiling: false,
  lastCompilationResult: null,
  autoCompile: true,

  searchQuery: '',
  searchResults: [],
  isSearching: false,
  searchOptions: {
    caseSensitive: false,
    wholeWord: false,
    useRegex: false,
  },

  outputMessages: [],

  settings: DEFAULT_EDITOR_SETTINGS,
};

interface ScriptEditorStore extends ScriptEditorState, ScriptEditorActions {}

// Helper functions for demo content
function getDemoScriptContent(fileName: string, language: 'typescript' | 'javascript'): string {
  if (fileName === 'PlayerController.ts' || fileName === 'PlayerController.js') {
    return language === 'typescript' ? `import { Component, Vector2 } from 'pixi-engine';

/**
 * PlayerController - Handles player movement and input
 */
export class PlayerController extends Component {
  private speed: number = 200;
  private velocity: Vector2 = { x: 0, y: 0 };

  onStart(): void {
    console.log('PlayerController started');
  }

  onUpdate(deltaTime: number): void {
    this.handleInput();
    this.move(deltaTime);
  }

  private handleInput(): void {
    this.velocity.x = 0;
    this.velocity.y = 0;

    if (Input.isKeyDown('ArrowLeft') || Input.isKeyDown('KeyA')) {
      this.velocity.x = -1;
    }
    if (Input.isKeyDown('ArrowRight') || Input.isKeyDown('KeyD')) {
      this.velocity.x = 1;
    }
    if (Input.isKeyDown('ArrowUp') || Input.isKeyDown('KeyW')) {
      this.velocity.y = -1;
    }
    if (Input.isKeyDown('ArrowDown') || Input.isKeyDown('KeyS')) {
      this.velocity.y = 1;
    }
  }

  private move(deltaTime: number): void {
    const transform = this.gameObject.transform;
    transform.position.x += this.velocity.x * this.speed * deltaTime;
    transform.position.y += this.velocity.y * this.speed * deltaTime;
  }
}
` : `/**
 * PlayerController - Handles player movement and input
 */
export class PlayerController {
  speed = 200;
  velocity = { x: 0, y: 0 };

  onStart() {
    console.log('PlayerController started');
  }

  onUpdate(deltaTime) {
    this.handleInput();
    this.move(deltaTime);
  }

  handleInput() {
    this.velocity.x = 0;
    this.velocity.y = 0;

    if (Input.isKeyDown('ArrowLeft') || Input.isKeyDown('KeyA')) {
      this.velocity.x = -1;
    }
    if (Input.isKeyDown('ArrowRight') || Input.isKeyDown('KeyD')) {
      this.velocity.x = 1;
    }
  }

  move(deltaTime) {
    const transform = this.gameObject.transform;
    transform.position.x += this.velocity.x * this.speed * deltaTime;
    transform.position.y += this.velocity.y * this.speed * deltaTime;
  }
}
`;
  }

  return language === 'typescript'
    ? `import { Component } from 'pixi-engine';

export class ${fileName.replace(/\.(ts|js)$/, '')} extends Component {
  onStart(): void {
    // Called when the component starts
  }

  onUpdate(deltaTime: number): void {
    // Called every frame
  }
}
`
    : `export class ${fileName.replace(/\.(ts|js)$/, '')} {
  onStart() {
    // Called when the component starts
  }

  onUpdate(deltaTime) {
    // Called every frame
  }
}
`;
}

function getTypeScriptTemplate(fileName: string): string {
  const className = fileName.replace(/\.ts$/, '');
  return `import { Component } from 'pixi-engine';

/**
 * ${className}
 *
 * TODO: Add description
 */
export class ${className} extends Component {
  // Properties
  private exampleProperty: number = 0;

  /**
   * Called when the component starts
   */
  onStart(): void {

  }

  /**
   * Called every frame
   * @param deltaTime Time since last frame in seconds
   */
  onUpdate(deltaTime: number): void {

  }

  /**
   * Called when the component is destroyed
   */
  onDestroy(): void {

  }
}
`;
}

function getJavaScriptTemplate(fileName: string): string {
  const className = fileName.replace(/\.js$/, '');
  return `/**
 * ${className}
 *
 * TODO: Add description
 */
export class ${className} {
  // Properties
  exampleProperty = 0;

  /**
   * Called when the component starts
   */
  onStart() {

  }

  /**
   * Called every frame
   * @param {number} deltaTime Time since last frame in seconds
   */
  onUpdate(deltaTime) {

  }

  /**
   * Called when the component is destroyed
   */
  onDestroy() {

  }
}
`;
}

function getDefaultTemplate(fileName: string, language: 'typescript' | 'javascript'): string {
  // Use the existing template functions for consistency
  if (language === 'typescript') {
    return getTypeScriptTemplate(fileName);
  }
  return getJavaScriptTemplate(fileName);
}

export const useScriptEditorStore = create<ScriptEditorStore>()(
  immer((set, get) => ({
      ...initialState,

      // File operations
      openFile: async (filePath: string) => {
        // Check if already loading this file (prevent race condition on rapid clicks)
        if (get().loadingFiles.has(filePath)) {
          return;
        }

        const existingFile = Object.values(get().files).find(f => f.path === filePath);

        if (existingFile) {
          // File already open, just activate it
          set(state => {
            state.activeTabId = existingFile.id;

            // Check if tab exists
            const tabExists = state.openTabs.some(t => t.fileId === existingFile.id);
            if (!tabExists) {
              state.openTabs.push({
                fileId: existingFile.id,
                isPinned: false,
                isPreview: false,
              });
            }
          });
          return;
        }

        // Mark file as loading
        set(state => {
          state.loadingFiles.add(filePath);
        });

        try {
          // Load file content from file system
          const fileName = filePath.split('/').pop() || filePath.split('\\').pop() || 'untitled';
          const extension = fileName.split('.').pop()?.toLowerCase();
          const language = extension === 'ts' || extension === 'tsx' ? 'typescript' : 'javascript';

          // Read content from file system if project root is set, otherwise use template
          const { readFile, hasProjectRoot } = await import('../services/FileSystemService');
          let content = '';
          if (hasProjectRoot()) {
            try {
              content = await readFile(filePath);
            } catch (error) {
              console.warn(`[openFile] Failed to read file, using template: ${error}`);
              content = getDefaultTemplate(fileName, language);
            }
          } else {
            // No project root, use demo content for development/testing
            content = getDemoScriptContent(fileName, language);
          }

          const fileId = generateId();
          const newFile: ScriptFile = {
            id: fileId,
            name: fileName,
            path: filePath,
            content,
            language,
            isDirty: false,
            lastSaved: new Date(),
            cursorPosition: { line: 1, column: 1 },
            scrollPosition: { top: 0, left: 0 },
          };

          set(state => {
            state.files[fileId] = newFile;

            // Remove any preview tabs
            state.openTabs = state.openTabs.filter(t => !t.isPreview);

            state.openTabs.push({
              fileId,
              isPinned: false,
              isPreview: false,
            });
            state.activeTabId = fileId;
          });
        } finally {
          // Remove from loading set
          set(state => {
            state.loadingFiles.delete(filePath);
          });
        }
      },

      closeFile: (fileId: string) => {
        set(state => {
          const tabIndex = state.openTabs.findIndex(t => t.fileId === fileId);
          if (tabIndex === -1) return;

          state.openTabs.splice(tabIndex, 1);
          delete state.files[fileId];

          // Update active tab if needed
          if (state.activeTabId === fileId) {
            if (state.openTabs.length > 0) {
              const newActiveIndex = Math.min(tabIndex, state.openTabs.length - 1);
              state.activeTabId = state.openTabs[newActiveIndex].fileId;
            } else {
              state.activeTabId = null;
            }
          }
        });
      },

      closeAllFiles: () => {
        set(state => {
          state.openTabs = [];
          state.files = {};
          state.activeTabId = null;
        });
      },

      closeOtherFiles: (fileId: string) => {
        set(state => {
          const keepFile = state.files[fileId];
          const keepTab = state.openTabs.find(t => t.fileId === fileId);

          if (keepFile && keepTab) {
            state.files = { [fileId]: keepFile };
            state.openTabs = [keepTab];
            state.activeTabId = fileId;
          }
        });
      },

      saveFile: async (fileId: string) => {
        const file = get().files[fileId];
        if (!file) return;

        // Virtual backend config files — sync JSON back to component data
        if (file.path.startsWith('virtual://') && fileId.startsWith('backend-config-')) {
          const objectId = fileId.replace('backend-config-', '');
          try {
            const parsed = JSON.parse(file.content);

            // Write parsed config back to the scene store component data
            const { useSceneStore } = await import('./sceneStore');
            const sceneState = useSceneStore.getState();
            const gameObject = sceneState.gameObjects[objectId];
            if (!gameObject) {
              throw new Error(`Game object "${objectId}" not found`);
            }
            const configComponent = gameObject.components.find(
              (c: { type: string }) => c.type === 'backend-game-config'
            );
            if (!configComponent) {
              throw new Error('backend-game-config component not found');
            }
            sceneState.updateComponent(objectId, configComponent.id, {
              backendConfig: parsed,
            });

            set(state => {
              if (state.files[fileId]) {
                state.files[fileId].isDirty = false;
                state.files[fileId].lastSaved = new Date();
              }
            });
            get().addOutputMessage({
              type: 'success',
              message: `Config applied: ${file.name}`,
              source: 'Editor',
            });
          } catch (err) {
            const message = err instanceof SyntaxError
              ? `Invalid JSON: ${err.message}`
              : `Failed to apply config: ${(err as Error).message}`;
            get().addOutputMessage({
              type: 'error',
              message,
              source: 'Editor',
            });
            // Show the problems panel so the user sees the error
            set(state => {
              state.activeBottomTab = 'output';
              state.bottomPanelVisible = true;
            });
          }
          return;
        }

        // Skip other virtual files
        if (file.path.startsWith('virtual://')) {
          return;
        }

        try {
          // Write to disk first
          const { writeFile, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            await writeFile(file.path, file.content);
          } else {
            console.log(`[saveFile] No project root, skipping disk write for: ${file.path}`);
          }

          // Only update store if disk write succeeds
          set(state => {
            if (state.files[fileId]) {
              state.files[fileId].isDirty = false;
              state.files[fileId].lastSaved = new Date();
            }
          });

          // Add output message
          get().addOutputMessage({
            type: 'success',
            message: `Saved: ${file.name}`,
            source: 'Editor',
          });

          // Trigger compilation if auto-compile is enabled
          if (get().autoCompile) {
            get().compile();
          }
        } catch (error) {
          get().addOutputMessage({
            type: 'error',
            message: `Failed to save ${file.name}: ${(error as Error).message}`,
            source: 'Editor',
          });
          throw error;
        }
      },

      saveAllFiles: async () => {
        const dirtyFiles = Object.values(get().files).filter(f => f.isDirty);
        for (const file of dirtyFiles) {
          await get().saveFile(file.id);
        }
      },

      createFile: async (path: string, language: 'typescript' | 'javascript') => {
        const fileName = path.split('/').pop() || path.split('\\').pop() || 'untitled';
        const fileId = generateId();

        const template = language === 'typescript'
          ? getTypeScriptTemplate(fileName)
          : getJavaScriptTemplate(fileName);

        try {
          // Write to disk first
          const { writeFile, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            await writeFile(path, template);
          } else {
            console.log(`[createFile] No project root, skipping disk write for: ${path}`);
          }

          // Only update store if disk write succeeds
          const newFile: ScriptFile = {
            id: fileId,
            name: fileName,
            path,
            content: template,
            language,
            isDirty: false, // Not dirty since we just wrote to disk
            lastSaved: new Date(),
            cursorPosition: { line: 1, column: 1 },
            scrollPosition: { top: 0, left: 0 },
          };

          set(state => {
            state.files[fileId] = newFile;
            state.openTabs.push({
              fileId,
              isPinned: false,
              isPreview: false,
            });
            state.activeTabId = fileId;

            // Add file to fileTree
            const pathParts = path.split('/');
            const parentPath = pathParts.slice(0, -1).join('/');

            // Find parent folder by path
            const parentNode = Object.values(state.fileTree).find(
              node => node.type === 'folder' && node.path === parentPath
            );

            const newFileNode: FileSystemNode = {
              id: fileId,
              name: fileName,
              path,
              type: 'file',
              parentId: parentNode?.id || null,
              language,
            };

            state.fileTree[fileId] = newFileNode;

            // Update parent folder's children array
            if (parentNode && state.fileTree[parentNode.id]) {
              const parent = state.fileTree[parentNode.id];
              if (!parent.children) {
                parent.children = [];
              }
              parent.children.push(fileId);
            }
          });

          // Refresh project panel to show the new file
          try {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            console.log('[ScriptEditor] Project panel refreshed after createFile');
          } catch (err) {
            console.warn('[ScriptEditor] Failed to refresh project panel:', err);
          }

          return fileId;
        } catch (error) {
          console.error(`[createFile] Failed to create file ${path}:`, error);
          throw error;
        }
      },

      deleteFile: async (fileId: string) => {
        get().closeFile(fileId);
        // In real app, delete from file system
      },

      renameFile: async (fileId: string, newName: string) => {
        set(state => {
          if (state.files[fileId]) {
            const oldPath = state.files[fileId].path;
            const pathParts = oldPath.split('/');
            pathParts[pathParts.length - 1] = newName;

            state.files[fileId].name = newName;
            state.files[fileId].path = pathParts.join('/');
            state.files[fileId].isDirty = true;
          }
        });
      },

      createFolder: async (parentPath: string, name: string) => {
        const folderId = generateId();
        const folderPath = parentPath ? `${parentPath}/${name}` : name;

        try {
          // Create directory on disk first
          const { createDirectory, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            await createDirectory(folderPath, true);
          } else {
            console.log(`[createFolder] No project root, skipping disk creation for: ${folderPath}`);
          }

          // Only update store if disk creation succeeds
          set(state => {
            // Find parent folder by path
            const parentNode = Object.values(state.fileTree).find(
              node => node.type === 'folder' && node.path === parentPath
            );

            const newFolderNode: FileSystemNode = {
              id: folderId,
              name,
              path: folderPath,
              type: 'folder',
              children: [],
              parentId: parentNode?.id || null,
            };

            state.fileTree[folderId] = newFolderNode;

            // Update parent folder's children array
            if (parentNode && state.fileTree[parentNode.id]) {
              const parent = state.fileTree[parentNode.id];
              if (!parent.children) {
                parent.children = [];
              }
              parent.children.push(folderId);
            }
          });

          // Refresh project panel to show the new folder
          try {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            console.log('[ScriptEditor] Project panel refreshed after createFolder');
          } catch (err) {
            console.warn('[ScriptEditor] Failed to refresh project panel:', err);
          }

          return folderId;
        } catch (error) {
          console.error(`[createFolder] Failed to create folder ${folderPath}:`, error);
          throw error;
        }
      },

      renameNode: async (nodeId: string, newName: string) => {
        const state = get();
        const node = state.fileTree[nodeId];
        if (!node) return;

        const oldPath = node.path;
        const pathParts = oldPath.split('/');
        pathParts[pathParts.length - 1] = newName;
        const newPath = pathParts.join('/');

        try {
          // Rename on disk first
          const { readFile, writeFile, deleteFile, deleteDirectory, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            if (node.type === 'file') {
              // For files: read content, write to new path, delete old
              const content = await readFile(oldPath);
              await writeFile(newPath, content);
              await deleteFile(oldPath);
            } else {
              // For folders: we need to move all contents recursively
              // This is complex with File System Access API, so we'll sync from disk after
              // For now, just update the store and let user know they need to refresh
              console.warn('[ScriptEditor] Folder rename on disk not fully supported - syncing from disk');
            }
          }

          // Update store
          set(draft => {
            const draftNode = draft.fileTree[nodeId];
            if (!draftNode) return;

            // Update node name and path
            draftNode.name = newName;
            draftNode.path = newPath;

            // If it's a folder, recursively update children paths
            if (draftNode.type === 'folder' && draftNode.children) {
              const updateChildPaths = (childIds: string[], oldBasePath: string, newBasePath: string) => {
                for (const childId of childIds) {
                  const child = draft.fileTree[childId];
                  if (child) {
                    child.path = child.path.replace(oldBasePath, newBasePath);
                    if (child.type === 'folder' && child.children) {
                      updateChildPaths(child.children, oldBasePath, newBasePath);
                    }
                    // Also update the corresponding ScriptFile if it's a file
                    if (child.type === 'file' && draft.files[childId]) {
                      draft.files[childId].path = child.path;
                      draft.files[childId].name = child.name;
                    }
                  }
                }
              };
              updateChildPaths(draftNode.children, oldPath, newPath);
            }

            // If it's a file, also update the ScriptFile record
            if (draftNode.type === 'file' && draft.files[nodeId]) {
              draft.files[nodeId].name = newName;
              draft.files[nodeId].path = newPath;
              draft.files[nodeId].isDirty = false; // File was written to disk
            }
          });

          // Refresh project panel
          try {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            console.log('[ScriptEditor] Project panel refreshed after renameNode');
          } catch (err) {
            console.warn('[ScriptEditor] Failed to refresh project panel:', err);
          }
        } catch (error) {
          console.error(`[ScriptEditor] Failed to rename ${oldPath} to ${newPath}:`, error);
          throw error;
        }
      },

      deleteNode: async (nodeId: string) => {
        const state = get();
        const node = state.fileTree[nodeId];
        if (!node) return;

        try {
          // Delete from disk first
          const { deleteFile, deleteDirectory, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            if (node.type === 'file') {
              await deleteFile(node.path);
            } else {
              // Delete folder recursively
              await deleteDirectory(node.path, true);
            }
          }

          // Update store after disk deletion succeeds
          set(draft => {
            const draftNode = draft.fileTree[nodeId];
            if (!draftNode) return;

            // Helper to recursively collect all node IDs to delete
            const collectNodeIds = (id: string): string[] => {
              const n = draft.fileTree[id];
              if (!n) return [];
              if (n.type === 'file') return [id];
              const childIds = n.children || [];
              return [id, ...childIds.flatMap(collectNodeIds)];
            };

            const idsToDelete = collectNodeIds(nodeId);

            // Remove from parent's children array
            if (draftNode.parentId && draft.fileTree[draftNode.parentId]) {
              const parent = draft.fileTree[draftNode.parentId];
              if (parent.children) {
                parent.children = parent.children.filter(id => id !== nodeId);
              }
            }

            // Delete all collected nodes and their corresponding files
            for (const id of idsToDelete) {
              const n = draft.fileTree[id];
              if (n?.type === 'file') {
                // Close the file tab if open
                const tabIndex = draft.openTabs.findIndex(t => t.fileId === id);
                if (tabIndex !== -1) {
                  draft.openTabs.splice(tabIndex, 1);
                  if (draft.activeTabId === id) {
                    draft.activeTabId = draft.openTabs.length > 0
                      ? draft.openTabs[Math.min(tabIndex, draft.openTabs.length - 1)].fileId
                      : null;
                  }
                }
                delete draft.files[id];
              }
              delete draft.fileTree[id];
            }
          });

          // Refresh project panel
          try {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            console.log('[ScriptEditor] Project panel refreshed after deleteNode');
          } catch (err) {
            console.warn('[ScriptEditor] Failed to refresh project panel:', err);
          }
        } catch (error) {
          console.error(`[ScriptEditor] Failed to delete ${node.path}:`, error);
          throw error;
        }
      },

      moveNode: async (nodeId: string, newParentId: string) => {
        const state = get();
        const node = state.fileTree[nodeId];
        const newParent = state.fileTree[newParentId];
        if (!node || !newParent || newParent.type !== 'folder') return;

        const oldPath = node.path;
        const newPath = `${newParent.path}/${node.name}`;

        try {
          // Move on disk first
          const { readFile, writeFile, deleteFile, deleteDirectory, hasProjectRoot } = await import('../services/FileSystemService');

          if (hasProjectRoot()) {
            if (node.type === 'file') {
              // For files: read content, write to new path, delete old
              const content = await readFile(oldPath);
              await writeFile(newPath, content);
              await deleteFile(oldPath);
            } else {
              // For folders: File System Access API doesn't support direct move
              // We'd need to recursively copy and delete, which is complex
              console.warn('[ScriptEditor] Folder move on disk not fully supported - syncing from disk');
            }
          }

          // Update store
          set(draft => {
            const draftNode = draft.fileTree[nodeId];
            const draftNewParent = draft.fileTree[newParentId];
            if (!draftNode || !draftNewParent || draftNewParent.type !== 'folder') return;

            const oldParentId = draftNode.parentId;

            // Remove from old parent's children
            if (oldParentId && draft.fileTree[oldParentId]) {
              const oldParent = draft.fileTree[oldParentId];
              if (oldParent.children) {
                oldParent.children = oldParent.children.filter(id => id !== nodeId);
              }
            }

            // Add to new parent's children
            if (!draftNewParent.children) {
              draftNewParent.children = [];
            }
            draftNewParent.children.push(nodeId);

            // Update node's parentId and path
            const draftOldPath = draftNode.path;
            const draftNewPath = `${draftNewParent.path}/${draftNode.name}`;
            draftNode.parentId = newParentId;
            draftNode.path = draftNewPath;

            // Recursively update children paths if it's a folder
            if (draftNode.type === 'folder' && draftNode.children) {
              const updateChildPaths = (childIds: string[], oldBasePath: string, newBasePath: string) => {
                for (const childId of childIds) {
                  const child = draft.fileTree[childId];
                  if (child) {
                    child.path = child.path.replace(oldBasePath, newBasePath);
                    if (child.type === 'folder' && child.children) {
                      updateChildPaths(child.children, oldBasePath, newBasePath);
                    }
                    // Also update the corresponding ScriptFile if it's a file
                    if (child.type === 'file' && draft.files[childId]) {
                      draft.files[childId].path = child.path;
                    }
                  }
                }
              };
              updateChildPaths(draftNode.children, draftOldPath, draftNewPath);
            }

            // If it's a file, also update the ScriptFile record
            if (draftNode.type === 'file' && draft.files[nodeId]) {
              draft.files[nodeId].path = draftNewPath;
            }
          });

          // Refresh project panel
          try {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            console.log('[ScriptEditor] Project panel refreshed after moveNode');
          } catch (err) {
            console.warn('[ScriptEditor] Failed to refresh project panel:', err);
          }
        } catch (error) {
          console.error(`[ScriptEditor] Failed to move ${oldPath} to ${newPath}:`, error);
          throw error;
        }
      },

      getNodeChildren: (nodeId: string) => {
        const state = get();
        const node = state.fileTree[nodeId];
        if (!node || node.type !== 'folder' || !node.children) {
          return [];
        }

        const children = node.children
          .map(id => state.fileTree[id])
          .filter((n): n is FileSystemNode => n !== undefined);

        // Sort: folders first (alphabetically), then files (alphabetically)
        return children.sort((a, b) => {
          if (a.type === 'folder' && b.type === 'file') return -1;
          if (a.type === 'file' && b.type === 'folder') return 1;
          return a.name.localeCompare(b.name);
        });
      },

      // Tab operations
      setActiveTab: (fileId: string) => {
        set(state => {
          state.activeTabId = fileId;
        });
      },

      reorderTabs: (fromIndex: number, toIndex: number) => {
        set(state => {
          const [tab] = state.openTabs.splice(fromIndex, 1);
          state.openTabs.splice(toIndex, 0, tab);
        });
      },

      pinTab: (fileId: string) => {
        set(state => {
          const tab = state.openTabs.find(t => t.fileId === fileId);
          if (tab) {
            tab.isPinned = true;
            tab.isPreview = false;
          }
        });
      },

      unpinTab: (fileId: string) => {
        set(state => {
          const tab = state.openTabs.find(t => t.fileId === fileId);
          if (tab) {
            tab.isPinned = false;
          }
        });
      },

      // Content operations
      updateFileContent: (fileId: string, content: string) => {
        set(state => {
          if (state.files[fileId]) {
            state.files[fileId].content = content;
            state.files[fileId].isDirty = true;
          }
        });
      },

      updateCursorPosition: (fileId: string, line: number, column: number) => {
        set(state => {
          if (state.files[fileId]) {
            state.files[fileId].cursorPosition = { line, column };
          }
        });
      },

      updateScrollPosition: (fileId: string, top: number, left: number) => {
        set(state => {
          if (state.files[fileId]) {
            state.files[fileId].scrollPosition = { top, left };
          }
        });
      },

      // Sidebar
      toggleSidebar: () => {
        set(state => {
          state.sidebarVisible = !state.sidebarVisible;
        });
      },

      setSidebarWidth: (width: number) => {
        set(state => {
          state.sidebarWidth = Math.max(150, Math.min(500, width));
        });
      },

      setActiveSidebarTab: (tab: SidebarTab) => {
        set(state => {
          state.activeSidebarTab = tab;
          if (!state.sidebarVisible) {
            state.sidebarVisible = true;
          }
        });
      },

      // Bottom panel
      toggleBottomPanel: () => {
        set(state => {
          state.bottomPanelVisible = !state.bottomPanelVisible;
        });
      },

      setBottomPanelHeight: (height: number) => {
        set(state => {
          state.bottomPanelHeight = Math.max(100, Math.min(500, height));
        });
      },

      setActiveBottomTab: (tab: BottomTab) => {
        set(state => {
          state.activeBottomTab = tab;
          if (!state.bottomPanelVisible) {
            state.bottomPanelVisible = true;
          }
        });
      },

      // Compilation
      compile: async () => {
        set(state => {
          state.isCompiling = true;
        });

        const startTime = Date.now();

        // Mock compilation - in real app, use TypeScript compiler API
        await new Promise(resolve => setTimeout(resolve, 500));

        const files = get().files;
        // Use Map for deduplication by location key
        const diagnosticMap = new Map<string, DiagnosticMessage>();

        // Simple mock validation - check for common issues
        Object.values(files).forEach(file => {
          const lines = file.content.split('\n');
          lines.forEach((line, index) => {
            // Check for console.log (warning)
            if (line.includes('console.log')) {
              const column = line.indexOf('console.log') + 1;
              const key = `${file.id}:${index + 1}:${column}:console.log`;

              if (!diagnosticMap.has(key)) {
                diagnosticMap.set(key, {
                  id: key, // Use deterministic key instead of generateId()
                  fileId: file.id,
                  filePath: file.path,
                  severity: 'warning',
                  message: 'Unexpected console statement',
                  line: index + 1,
                  column,
                  endLine: index + 1,
                  endColumn: column + 'console.log'.length,
                  source: 'eslint',
                });
              }
            }

            // Check for 'any' type (warning for TypeScript)
            if (file.language === 'typescript' && line.includes(': any')) {
              const column = line.indexOf(': any') + 3;
              const key = `${file.id}:${index + 1}:${column}:any`;

              if (!diagnosticMap.has(key)) {
                diagnosticMap.set(key, {
                  id: key, // Use deterministic key instead of generateId()
                  fileId: file.id,
                  filePath: file.path,
                  severity: 'warning',
                  message: "Unexpected 'any'. Specify a different type.",
                  line: index + 1,
                  column,
                  endLine: index + 1,
                  endColumn: column + 3,
                  code: 7006,
                  source: 'typescript',
                });
              }
            }
          });
        });

        const diagnostics = Array.from(diagnosticMap.values());

        const result: CompilationResult = {
          success: !diagnostics.some(d => d.severity === 'error'),
          outputFiles: [],
          diagnostics,
          duration: Date.now() - startTime,
        };

        set(state => {
          state.isCompiling = false;
          state.lastCompilationResult = result;
          state.diagnostics = diagnostics;
          state.diagnosticCounts = {
            errors: diagnostics.filter(d => d.severity === 'error').length,
            warnings: diagnostics.filter(d => d.severity === 'warning').length,
            infos: diagnostics.filter(d => d.severity === 'info' || d.severity === 'hint').length,
          };
        });

        get().addOutputMessage({
          type: result.success ? 'success' : 'error',
          message: result.success
            ? `Build succeeded in ${result.duration}ms`
            : `Build failed with ${result.diagnostics.filter(d => d.severity === 'error').length} errors`,
          source: 'TypeScript',
        });

        return result;
      },

      setAutoCompile: (enabled: boolean) => {
        set(state => {
          state.autoCompile = enabled;
        });
      },

      clearDiagnostics: () => {
        set(state => {
          state.diagnostics = [];
          state.diagnosticCounts = { errors: 0, warnings: 0, infos: 0 };
        });
      },

      // Search
      search: (query: string) => {
        set(state => {
          state.searchQuery = query;
          state.isSearching = true;
        });

        const { files, searchOptions } = get();
        const results: SearchResult[] = [];

        if (query.trim()) {
          Object.values(files).forEach(file => {
            const lines = file.content.split('\n');
            lines.forEach((line, index) => {
              let searchStr = query;
              let lineStr = line;

              if (!searchOptions.caseSensitive) {
                searchStr = searchStr.toLowerCase();
                lineStr = lineStr.toLowerCase();
              }

              let searchIndex = 0;
              while ((searchIndex = lineStr.indexOf(searchStr, searchIndex)) !== -1) {
                if (searchOptions.wholeWord) {
                  const before = lineStr[searchIndex - 1];
                  const after = lineStr[searchIndex + searchStr.length];
                  if ((before && /\w/.test(before)) || (after && /\w/.test(after))) {
                    searchIndex++;
                    continue;
                  }
                }

                results.push({
                  fileId: file.id,
                  filePath: file.path,
                  line: index + 1,
                  column: searchIndex + 1,
                  matchLength: query.length,
                  lineContent: line,
                });
                searchIndex++;
              }
            });
          });
        }

        set(state => {
          state.searchResults = results;
          state.isSearching = false;
        });
      },

      setSearchOptions: (options) => {
        set(state => {
          state.searchOptions = { ...state.searchOptions, ...options };
        });
        // Re-run search with new options
        if (get().searchQuery) {
          get().search(get().searchQuery);
        }
      },

      clearSearch: () => {
        set(state => {
          state.searchQuery = '';
          state.searchResults = [];
        });
      },

      goToSearchResult: (result: SearchResult) => {
        set(state => {
          state.activeTabId = result.fileId;
          if (state.files[result.fileId]) {
            state.files[result.fileId].cursorPosition = {
              line: result.line,
              column: result.column,
            };
          }
        });
      },

      // Output
      addOutputMessage: (message) => {
        set(state => {
          state.outputMessages.unshift({
            ...message,
            id: generateId(),
            timestamp: new Date(),
          });
          // Keep only last 1000 messages
          if (state.outputMessages.length > 1000) {
            state.outputMessages = state.outputMessages.slice(0, 1000);
          }
        });
      },

      clearOutput: () => {
        set(state => {
          state.outputMessages = [];
        });
      },

      // Settings
      updateSettings: (settings) => {
        set(state => {
          state.settings = { ...state.settings, ...settings };
        });
      },

      // Open a virtual JSON config in a new tab (for backend game inspection)
      openJsonConfig: (objectId: string, _componentId: string, title: string, content: string) => {
        const id = `backend-config-${objectId}`;
        set((state) => {
          // Always refresh content from the backend config (read-only view)
          const existing = state.files[id];
          if (existing) {
            existing.content = content;
            existing.name = title;
            existing.isDirty = false;
          } else {
            state.files[id] = {
              id,
              name: title,
              path: `virtual://${id}.json`,
              content,
              language: 'javascript' as 'javascript',
              isDirty: false,
              lastSaved: new Date(),
              cursorPosition: { line: 1, column: 1 },
              scrollPosition: { top: 0, left: 0 },
            };
          }
          // Only add tab if not already open
          const tabExists = state.openTabs.some(t => t.fileId === id);
          if (!tabExists) {
            state.openTabs = state.openTabs.filter(t => !t.isPreview);
            state.openTabs.push({ fileId: id, isPinned: false, isPreview: false });
          }
          state.activeTabId = id;
        });
      },

      // Project state methods
      loadProjectState: (projectState: {
        openTabs?: OpenTab[];
        activeTabId?: string | null;
        sidebarWidth?: number;
        sidebarVisible?: boolean;
        bottomPanelHeight?: number;
        bottomPanelVisible?: boolean;
      }) => {
        set((draft) => {
          // Restore open tabs metadata (content will be reloaded from disk)
          draft.openTabs = (projectState.openTabs || []).map((tab: OpenTab) => ({
            ...tab,
            isPreview: false,
          }));
          draft.activeTabId = projectState.activeTabId ?? null;
          draft.sidebarWidth = projectState.sidebarWidth ?? 250;
          draft.sidebarVisible = projectState.sidebarVisible ?? true;
          draft.bottomPanelHeight = projectState.bottomPanelHeight ?? 200;
          draft.bottomPanelVisible = projectState.bottomPanelVisible ?? true;
          // Note: actual file content will be reloaded by the component via openFile
        });
      },

      getProjectState: () => {
        const state = get();
        return {
          openTabs: (state.openTabs || []).map((tab) => ({
            fileId: tab.fileId,
            isPinned: tab.isPinned || false,
            isPreview: tab.isPreview || false,
          })),
          activeTabId: state.activeTabId,
          sidebarWidth: state.sidebarWidth ?? 250,
          sidebarVisible: state.sidebarVisible ?? true,
          bottomPanelHeight: state.bottomPanelHeight ?? 200,
          bottomPanelVisible: state.bottomPanelVisible ?? true,
          openFilePaths: Object.values(state.files).map((f) => f.path),
        };
      },

      resetProjectState: () => {
        set((draft) => {
          draft.openTabs = [];
          draft.activeTabId = null;
          draft.files = {};
          draft.diagnostics = [];
          draft.lastCompilationResult = null;
          draft.searchQuery = '';
          draft.searchResults = [];
          draft.outputMessages = [];
        });
      },
    }))
);
